export function formatLiveSince(value?: string | null): string {
  if (!value) return 'Live since -'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return 'Live since -'
  return `Live since ${d.toLocaleString('en-US', { month: 'short', year: 'numeric' })}`
}

export function normalizeDateInput(value: string): string | null {
  const raw = value.trim()
  if (!raw) return null

  // Accept native date input format directly.
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw

  // Also accept MM/DD/YYYY typed in text-like date fields (browser-dependent).
  const slashMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (!slashMatch) return null
  const month = Number(slashMatch[1])
  const day = Number(slashMatch[2])
  const year = Number(slashMatch[3])
  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/** Value for <input type="date" /> from API `lastVisitDate` (ISO date or datetime). */
export function lastVisitDateForInput(raw: string): string {
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10)
  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function visitsFieldError(visits: string): string | null {
  const t = visits.trim()
  if (!t) return 'Number of visits is required (at least 1).'
  if (!/^\d+$/.test(t)) return 'Visits must be a whole number.'
  const n = Number(t)
  if (n < 1) return 'Visits must be at least 1.'
  return null
}

export function daysSince(dateLike: string): number {
  const dt = new Date(dateLike)
  if (Number.isNaN(dt.getTime())) return 0
  return Math.max(0, Math.floor((Date.now() - dt.getTime()) / (1000 * 60 * 60 * 24)))
}

export function formatDateShort(dateLike: string): string {
  const dt = new Date(dateLike)
  if (Number.isNaN(dt.getTime())) return '-'
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export function formatDateTime(dateLike: string): string {
  const dt = new Date(dateLike)
  if (Number.isNaN(dt.getTime())) return '-'
  return dt.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export type LapsedHistoryItem = {
  label: string
  timestamp: string
  tone: 'pending' | 'sent' | 'replied'
}

export function buildLapsedHistory(
  row: {
    lastVisitDate: string
    normalizedStatus: 'pending' | 'sent' | 'replied'
    metadata?: Record<string, string>
  },
  lapseDays: number
): LapsedHistoryItem[] {
  const metadata = row.metadata ?? {}
  const items: LapsedHistoryItem[] = []

  const lapsedAt = new Date(row.lastVisitDate)
  if (!Number.isNaN(lapsedAt.getTime())) {
    lapsedAt.setDate(lapsedAt.getDate() + lapseDays)
    items.push({
      label: `Customer crossed lapse window (${lapseDays} days)`,
      timestamp: formatDateTime(lapsedAt.toISOString()),
      tone: 'pending',
    })
  }

  const lastSent =
    metadata.nudgeLastSentAt || metadata.lastNudgedAt || metadata.sentAt || metadata.nudgeSentAt
  if (lastSent) {
    items.push({
      label: 'Automated or manual nudge was sent',
      timestamp: formatDateTime(lastSent),
      tone: 'sent',
    })
  }

  const lastReply = metadata.nudgeLastReplyAt || metadata.lastReplyAt || metadata.repliedAt
  if (lastReply) {
    items.push({
      label: 'Customer replied',
      timestamp: formatDateTime(lastReply),
      tone: 'replied',
    })
  }

  if (items.length === 0) {
    return [{ label: 'No nudge history available yet', timestamp: '-', tone: 'pending' }]
  }

  return items
}

/**
 * Convert an HTML email body to plain text suitable for a chat bubble.
 * - Inserts newlines for <br>, <p>, <div>, <li>
 * - Strips style/script content
 * - Collapses excessive whitespace
 * Returns the input unchanged if it doesn't look like HTML.
 */
export function htmlToPlainText(value: string | null | undefined): string {
  if (!value) return ''
  const looksLikeHtml = /<[a-z!][\s\S]*?>/i.test(value)
  if (!looksLikeHtml) return value.trim()

  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return value
      .replace(/<style[\s\S]*?<\/style>/gi, '')
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/<\/(p|div|li|h\d|tr)>/gi, '\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  }

  const doc = new DOMParser().parseFromString(value, 'text/html')
  doc.querySelectorAll('style, script, head').forEach(el => el.remove())
  doc.querySelectorAll('br').forEach(br => br.replaceWith('\n'))
  doc.querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6, tr').forEach(el => {
    el.append('\n')
  })
  const text = doc.body?.textContent ?? ''
  return text
    // Collapse runs of spaces/tabs (caused by HTML source indentation inside
    // <span>/<td> wrapping) to a single space — otherwise lines render as
    // "        Hi Diniranga," in the chat panel.
    .replace(/[ \t]{2,}/g, ' ')
    // Trim trailing whitespace before any newline.
    .replace(/[ \t]+\n/g, '\n')
    // Trim leading whitespace after any newline.
    .replace(/\n[ \t]+/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/** Normalize text for dedup comparisons (lowercase, collapsed whitespace). */
export function normalizeForDedup(value: string): string {
  return value.toLowerCase().replace(/\s+/g, ' ').trim()
}

export function stripQuotedReply(text: string): string {
  if (!text) return text
  const lines = text.split('\n')
  let cutIndex = lines.length

  let firstQuoteIdx = -1
  for (let i = 0; i < lines.length; i++) {
    if (/^\s*>/.test(lines[i])) {
      firstQuoteIdx = i
      break
    }
  }

  if (firstQuoteIdx >= 0) {
    cutIndex = firstQuoteIdx
    let attribEnd = firstQuoteIdx - 1
    while (attribEnd >= 0 && lines[attribEnd].trim() === '') attribEnd--
    let attribStart = attribEnd
    while (attribStart - 1 >= 0 && lines[attribStart - 1].trim() !== '') attribStart--
    if (attribEnd >= 0) {
      const attrib = lines.slice(attribStart, attribEnd + 1).join(' ').trim()
      if (/^On\b.*wrote:\s*$/.test(attrib)) {
        cutIndex = attribStart
      }
    }
  } else {
    for (let i = 0; i < lines.length; i++) {
      if (/^On\b.*wrote:\s*$/.test(lines[i].trim())) {
        cutIndex = i
        break
      }
    }
  }

  return lines.slice(0, cutIndex).join('\n').replace(/\s+$/, '')
}

export function dateOnly(value: string): string {
  if (!value) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 10)
}

export function relativeTimeLabel(value: string): string {
  const dt = new Date(value)
  if (Number.isNaN(dt.getTime())) return '-'
  const diffMs = Date.now() - dt.getTime()
  const mins = Math.floor(diffMs / (1000 * 60))
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days === 1) return 'yesterday'
  return `${days}d ago`
}

export function toChartY(value: number, maxValue: number, top = 8, bottom = 100): number {
  if (maxValue <= 0) return bottom
  return bottom - (value / maxValue) * (bottom - top)
}

export function initialsFromName(fullName: string): string {
  const parts = fullName
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (parts.length === 0) return '?'
  return parts
    .slice(0, 2)
    .map(p => p[0]?.toUpperCase() ?? '')
    .join('')
}

/** Tag/segment — same pill pattern as Status (dot + rounded pill). */
export function contactTagPill(metadata?: Record<string, string>): { label: string; className: string } {
  const raw = (metadata?.tag || metadata?.segment || '').trim()
  if (!raw) {
    return { label: '—', className: 'pill pill-pending' }
  }
  const lower = raw.toLowerCase()
  let mod: 'pill-tag-blue' | 'pill-tag-teal' | 'pill-tag-orange' | 'pill-tag-green' =
    'pill-tag-teal'
  if (lower.includes('vip')) mod = 'pill-tag-orange'
  else if (lower.includes('regular')) mod = 'pill-tag-blue'
  else if (lower.includes('new')) mod = 'pill-tag-teal'
  else if (lower.includes('premium') || lower.includes('gold')) mod = 'pill-tag-orange'
  else if (lower.includes('member') || lower.includes('loyal')) mod = 'pill-tag-green'
  else {
    const palette: Array<'pill-tag-blue' | 'pill-tag-teal' | 'pill-tag-orange' | 'pill-tag-green'> =
      ['pill-tag-blue', 'pill-tag-teal', 'pill-tag-orange', 'pill-tag-green']
    const h = lower.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0)
    mod = palette[h % palette.length]
  }
  return { label: raw, className: `pill ${mod}` }
}
